/**
 * 수료증 인쇄 / PDF 페이지
 *  - 관리자: /admin/bt-cert/print?ids=a,b
 *  - 교회:   /report/print?ids=a,b
 * 인쇄 → 브라우저 인쇄창(프린터 또는 PDF로 저장), PDF → 파일 바로 다운로드. 둘 다 수령/인쇄 기록을 남김
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, ThemeProvider, Typography, CssBaseline } from '@mui/material';
import { api, CertData } from '../api';
import { yellowTheme, YELLOW_GRADIENT, ON_YELLOW } from '../common';
import { CertificateSheet, certName, downloadCertificatesPdf, waitImages } from './Certificate';

const CertificatePrintPage: React.FC<{ source: 'admin' | 'church' }> = ({ source }) => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [certs, setCerts] = useState<CertData[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const ids = params.get('ids') || '';
    const url = source === 'admin'
      ? `/admin/cert/certificates?status=issued&ids=${encodeURIComponent(ids)}`
      : `/church-portal/certificates?ids=${encodeURIComponent(ids)}`;
    api<{ data: CertData[] }>(url)
      .then((r) => setCerts(r.data))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [params, source]);

  const log = (type: 'print' | 'pdf') => api(source === 'admin' ? '/admin/cert/certificates/log' : '/church-portal/certificates/log', {
    body: { ids: certs.map((c) => c._id), type },
  }).catch(() => undefined);

  const handlePrint = async () => {
    if (noEn) return;
    await waitImages(document.querySelector('.bt-print-root') || document);
    log('print');
    window.print();
  };

  const handlePdf = async () => {
    setBusy(true);
    try {
      await downloadCertificatesPdf(certs);
      log('pdf');
    } catch (e: any) {
      setError(e.message || 'PDF를 만들지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };

  const noEn = certs.filter((c) => !certName(c)).length;
  return (
    <ThemeProvider theme={yellowTheme}>
      <CssBaseline />
      <Box sx={{ bgcolor: '#8a8f99', minHeight: '100vh' }}>
        <Box sx={{ position: 'sticky', top: 0, zIndex: 2, background: YELLOW_GRADIENT, color: ON_YELLOW, p: 1.5, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', '@media print': { display: 'none' } }}>
          <Button color="inherit" onClick={() => navigate(-1)}>← 뒤로</Button>
          <Typography fontWeight={800}>BT 수료증 {certs.length}장 · A4 가로</Typography>
          <Box flex={1} />
          <Button variant="outlined" color="inherit" onClick={handlePdf} disabled={!certs.length || busy || noEn > 0} sx={{ bgcolor: '#fff' }}>
            {busy ? 'PDF 만드는 중…' : '⬇ PDF 다운로드'}
          </Button>
          <Button variant="contained" color="secondary" onClick={handlePrint} disabled={!certs.length || noEn > 0}>
            🖨 인쇄
          </Button>
        </Box>
        {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}
        {noEn > 0 && (
          <Alert severity="warning" sx={{ borderRadius: 0, '@media print': { display: 'none' } }}>영문 이름을 확인할 교사가 {noEn}명 있습니다. 본부 관리자에게 교사 정보의 영문 이름을 입력하도록 요청한 후 다시 열어주세요.</Alert>
        )}
        <Alert severity="info" sx={{ borderRadius: 0, '@media print': { display: 'none' } }}>
          인쇄창에서 용지 <b>A4 · 가로</b>, 여백 <b>없음</b>, <b>배경 그래픽 켜기</b>를 선택하세요. 담임목사 서명란은 인쇄 후 직접 서명합니다.
        </Alert>
        {loading ? (
          <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 300 }}><CircularProgress /></Box>
        ) : (
          <Box className="bt-print-root" sx={{ py: 3, overflowX: 'auto', ...(noEn > 0 ? { '@media print': { display: 'none' } } : {}) }}>
            {certs.map((c) => <CertificateSheet key={c._id} cert={c} />)}
            {!certs.length && !error && <Typography sx={{ color: '#fff', textAlign: 'center', mt: 6 }}>인쇄할 수료증이 없습니다.</Typography>}
          </Box>
        )}
      </Box>
    </ThemeProvider>
  );
};

export default CertificatePrintPage;
